<template>
  <div class="search-container" :class="{ 'is-expanded': isExpanded || searchQuery.length > 0 }" @click="expandSearch">
    <button class="filter-btn" :class="{ 'active': showFilterMenu || selectedFilter !== 'plant' }"
      @click.stop="toggleFilterMenu" :title="$t('Filter Search')">
      <svg class="filter-icon-svg" viewBox="0 0 24 24">
        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        <circle cx="9" cy="6" r="2" fill="currentColor" />
        <circle cx="15" cy="12" r="2" fill="currentColor" />
        <circle cx="9" cy="18" r="2" fill="currentColor" />
      </svg>
    </button>

    <input ref="searchInput" v-model="searchQuery" :placeholder="dynamicPlaceholder" class="search-input" type="text"
      @focus="isExpanded = true" @keyup.enter="performSearch" @input="$emit('search-input-change', searchQuery)" />

    <div class="search-action-icon" @click="performSearch">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
    </div>

    <div v-if="showFilterMenu" class="filter-dropdown">
      <div v-for="(option, index) in filterOptions" :key="index" class="filter-option"
        :class="{ 'selected': selectedFilter === option.value }" @click.stop="selectFilter(option.value)">
        <span>{{ $t(option.label) }}</span>
        <span v-if="selectedFilter === option.value" class="check-icon">✓</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchFilterBar',
  props: {
    defaultQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchQuery: this.defaultQuery,
      isExpanded: false,
      showFilterMenu: false,
      selectedFilter: 'plant',
      filterOptions: [
        { label: 'Plant\'s name & Location', value: 'all' },
        { label: 'Plant\'s name', value: 'plant' },
        { label: 'Location', value: 'location' }
      ]
    }
  },
  computed: {
    dynamicPlaceholder() {
      switch (this.selectedFilter) {
        case 'location':
          return this.$t('Search by location');
        case 'all':
          return this.$t('Search plants or locations');
        case 'plant':
        default:
          return this.$t('Search by plant name');
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.closeAll();
      }
    },
    expandSearch() {
      this.isExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
      this.isExpanded = true;
    },
    selectFilter(value) {
      this.selectedFilter = value;
      this.showFilterMenu = false;
      this.$emit('filter-change', value);
      this.$refs.searchInput.focus();
    },
    closeAll() {
      if (this.searchQuery === '') {
        this.isExpanded = false;
      }
      this.showFilterMenu = false;
    },
    performSearch() {
      this.$emit('search-submit', {
        query: this.searchQuery,
        filter: this.selectedFilter
      });
      this.isExpanded = false;
      this.showFilterMenu = false;
    }
  }
}
</script>

<style scoped>
.search-container {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #213974 !important;
  border-radius: 50px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  width: 250px;
  z-index: 3 !important;
}

.search-container.is-expanded {
  width: 315px;
  box-shadow: 1px 1px rgba(126, 158, 247, 0.418);
}

.filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  margin-right: 8px;
  border-radius: 50%;
  transition: background 0.2s;
  flex-shrink: 0;
  background: transparent;
  border: none;
  outline: none;
}

.filter-btn:hover {
  color: #34656d !important;
  background-color: var(--v-bg-base) !important;
}

.filter-icon-svg {
  width: 22px;
  height: 22px;
  transition: fill 0.3s;
}

.search-input {
  border: none;
  outline: none;
  font-weight: 500;
  font-size: 14px;
  color: var(--primary-color);
  background: transparent;
  width: 100%;
  height: 32px;
  background-image: linear-gradient(to right, var(--grey-base) 0%, var(--grey-base) 100%);
  background-position: 0 100%;
  background-repeat: no-repeat;
  background-size: 0% 2px;
  transition: background-size 0.4s ease;
}

.search-container.is-expanded .search-input {
  background-size: 100% 2px;
}

.search-input::placeholder {
  color: #9e9e9e;
  font-weight: 400;
}

.search-action-icon {
  color: var(--extra-grey);
  margin-right: 8px;
  cursor: pointer;
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.3s ease;
}

.search-container.is-expanded .search-action-icon {
  opacity: 1;
  transform: translateX(0);
}

.filter-dropdown {
  width: 200px;
  padding: 8px;
  position: absolute;
  top: 120%;
  left: 0;
  background-color: var(--v-primary-base) !important;
  border-radius: 12px;
  animation: slideDown 0.2s ease-out;
  transform-origin: top left;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: scaleY(0.8);
  }

  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

.filter-option {
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
}

.filter-option:hover {
  background-color: var(--v-bg-base);
}

.filter-option.selected {
  background-color: #E9952A;
  font-weight: 500;
}

.check-icon {
  font-size: 12px;
  display: none;
}

.filter-option.selected .check-icon {
  display: block;
}

@media (max-width: 510px) {
  .search-container {
    width: 160px;
  }

  .search-container.is-expanded {
    width: 90vw;
  }
}
</style>